import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Select } from "antd";
import { useDebounce, useToggle } from "react-use";
import unionBy from "lodash-es/unionBy";
import isObject from "lodash-es/isObject";
import { useTranslation } from "react-i18next";
import { PartialListDto } from "../api/types";
import { ValueSelectItem } from "./ValueSelect";
import { MemberItem } from "./MemberItem";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { useInView } from "react-intersection-observer";

const PAGE_SIZE = 200;

interface ValueSelectInfiniteProps {
  /**
   * Data source
   */
  dataSource?: ValueSelectItem[];

  onChange?: (item: ValueSelectItem | undefined) => void;
  onClick?: () => void;
  onBlur?: (item: ValueSelectItem | undefined) => void;
  useIdAsValue?: boolean;
  style?: CSSProperties;
  value?: any;
  disabled?: boolean;
  id?: string;
  autoFocus?: boolean;
  allowClear?: boolean;
  className?: string;
  defaultOpen?: boolean;
  apiFunction?: (
    filter: {
      skip: number;
      max: number;
      fulltext?: string;
    },
    initialValue?: any,
    ac?: AbortController
  ) => Promise<PartialListDto<MemberItem>>;
  placeholder?: string;
  filter?: any;
  excludeItems?: ValueSelectItem[];
  disabledItems?: ValueSelectItem[];
  size?: SizeType;
  mode?: "multiple" | "tags";
  autoFill?: boolean;
  all?: boolean;
  disableSearch?: boolean;
  initialValue?: any;
  dataCy?: string;
  externalApiError?: any; // New prop to accept errors from parent components
  /** Rendered inside the control, before the value. */
  prefix?: React.ReactNode;
  /** Rendered before each option's name. Options keep their plain layout when this is unset. */
  optionPrefix?: (item: MemberItem) => React.ReactNode;
}

function getValue(val: any) {
  if (typeof val === "object") {
    if (val.name) {
      return { name: val.name };
    }
    return { name: val.id };
  }
  return { name: val };
}

function findItemByValue(value: ValueSelectItem, dataSource: ValueSelectItem[]) {
  if (isObject(value)) {
    return dataSource.find((item) => item.id === value?.id) ?? getValue(value);
  }
  return dataSource.find((item) => item.id === value) ?? getValue(value);
}

const ValueSelectInfinite = forwardRef<any, ValueSelectInfiniteProps>((props: ValueSelectInfiniteProps, parentRef) => {
  const localRef = useRef<any>(null);

  const { t } = useTranslation();
  const previousController = useRef<AbortController>(null);

  const [loadingMore, setLoadingMore] = useToggle(false);
  const [_currentOptions, setCurrentOptions] = useState<ValueSelectItem[]>(props.dataSource ?? []);
  const [apiError, setApiError] = useState<any>(null);

  // Merge external error with internal API error
  const effectiveApiError = props.externalApiError || apiError;

  useEffect(() => {
    setCurrentOptions(props.dataSource ?? []);
  }, [JSON.stringify(props.dataSource)]);

  const filteredOptions = _currentOptions?.filter((i) => !props.excludeItems?.find((v) => v.id === i.id)) ?? [];

  const [skip, setSkip] = useState<number>(0);
  const [fulltext, setFulltext] = useState<string | undefined>();
  const [debouncedFulltext, setDebouncedFulltext] = useState<string>();

  const [open, setOpen] = useState(false);
  // Expose the localRef to the parent
  useImperativeHandle(parentRef, () => ({
    focus: () => {
      if (localRef.current) {
        localRef.current.focus();
        setOpen(true);
      }
    },
  }));

  useDebounce(
    () => {
      setDebouncedFulltext(fulltext);
    },
    300,
    [fulltext]
  );

  const [reachedTheTop, setReachedTheTop] = useState<boolean>(false);
  function fetchMore2() {
    if (!props.apiFunction) {
      return;
    }

    // Cancel the previous request if it exists
    if (previousController.current) {
      previousController.current.abort();
    }

    // Create a new AbortController for the new request
    previousController.current = new AbortController();
    setLoadingMore(true);

    props
      ?.apiFunction?.(
        {
          ...props.filter,
          skip,
          max: PAGE_SIZE,
          fulltext: debouncedFulltext != "" ? debouncedFulltext : undefined,
        },
        props.initialValue,
        previousController.current
      )
      .then((result: PartialListDto<MemberItem>) => {
        setApiError(null); // Clear previous errors
        setLoadingMore(false);
        if (skip + PAGE_SIZE > result.rowCount) {
          setReachedTheTop(true);
        }
        if (result?.rowCount !== result?.results?.length) {
          setCurrentOptions((realCurrentOptions) => unionBy(realCurrentOptions, result.results, "id"));
        } else if (result?.rowCount === result?.results?.length) {
          setCurrentOptions(result.results);
        }
        /**
         * If the result contains only one record it will be filled in
         */
        if (props.autoFill) {
          if (result.rowCount === 1 && result.results.length === 1) {
            props?.onChange?.(result.results[0]);
          }
        }
      })
      .catch((error) => {
        setLoadingMore(false);
        setApiError(error);
        console.error("API Error:", error);
      });
  }

  const strFilter = JSON.stringify(props.filter);

  useEffect(() => {
    fetchMore2();
    return () => {
      if (previousController.current) {
        previousController.current.abort();
      }
    };
  }, [skip, debouncedFulltext, strFilter, props.autoFill]);

  function onFulltextChange(text: string | undefined) {
    setSkip(0);
    setFulltext(text);
    setReachedTheTop(false);
  }

  function extractValue() {
    if (!props.value) {
      return undefined;
    }
    if (props.mode == "multiple") {
      return props?.value?.map((i: any) => filteredOptions.find((j) => j.id == (i.id ?? i))).map((i: any) => i?.id);
    }
    return findItemByValue(props.value, filteredOptions);
  }

  //checks if the window is scrolled
  const [ref, inView] = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView) {
      setSkip((s) => s + PAGE_SIZE);
    }
  }, [inView]);
  const value = extractValue();

  function getOptions() {
    const opts = filteredOptions.map((i) => {
      const isDisabled = props.disabledItems?.find((v) => v?.id === i.id);
      const text = (
        <div className={props.optionPrefix ? "min-w-0 flex-1" : undefined}>
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.name}</div>
          {i.description && <div style={{ color: "#777" }}>{i.description}</div>}
        </div>
      );
      return {
        value: i.id,
        label: props.optionPrefix ? (
          <div className="flex items-center gap-[var(--gap-small)]">
            {props.optionPrefix(i)}
            {text}
          </div>
        ) : (
          text
        ),
        title: i.name,
        disabled: !!isDisabled,
      };
    });
    const needsLoadMore = props.apiFunction && !reachedTheTop && filteredOptions.length >= PAGE_SIZE;

    if (needsLoadMore && props.all) {
      return [{ value: "", label: t("All") }, ...opts, { value: -1, label: <div ref={ref} style={{ height: 5 }} /> }];
    }
    if (needsLoadMore) {
      return [...opts, { value: -1, label: <div ref={ref} style={{ height: 5 }} /> }];
    }

    return opts;
  }

  const isDisabledDueToError = effectiveApiError !== null;
  const is403Error = effectiveApiError?.status === 403;
  const showSearch = !props.disableSearch;
  return (
    <>
      <Select
        ref={localRef}
        allowClear={props.allowClear ?? true}
        autoFocus={props.autoFocus}
        className={props.className}
        data-cy={props.dataCy ?? "value-select"}
        defaultOpen={props.defaultOpen}
        disabled={props.disabled || isDisabledDueToError}
        id={props.id}
        listHeight={350}
        loading={loadingMore}
        maxTagCount={"responsive"}
        mode={props.mode}
        open={open}
        options={getOptions()}
        placeholder={is403Error ? t("Missing permissions") : props.placeholder}
        prefix={props.prefix}
        showSearch={showSearch}
        size={props.size ?? "middle"}
        {...(showSearch && {
          searchValue: fulltext,
          autoClearSearchValue: true,
          filterOption: false,
          onSearch: onFulltextChange,
          onInputKeyDown: (e: React.KeyboardEvent) => {
            // Prevent space from selecting the highlighted option —
            // allow it to be typed as part of the search text instead
            if (e.key === " ") {
              e.stopPropagation();
            }
          },
        })}
        style={{ minWidth: "5rem", ...props.style }}
        value={props.mode === "multiple" ? value : value?.name}
        onChange={(v: any) => {
          localRef.current?.blur();
          onFulltextChange(undefined);
          if (props.mode === "multiple") {
            const idArr = v.map((id: number) => filteredOptions.find((i) => i.id === id)?.id);
            props?.onChange?.(idArr);
          } else if (props.useIdAsValue) {
            props?.onChange?.(v);
          } else {
            const item = filteredOptions.find((i) => i.id === v);
            if (item) {
              props?.onChange?.({ ...item, id: item.id, name: item.name });
            } else {
              props?.onChange?.(undefined);
            }
          }
        }}
        onOpenChange={setOpen}
      />
      {is403Error && (
        <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
          {t("Unable to change because of missing permissions")}
        </div>
      )}
    </>
  );
});

export default ValueSelectInfinite;
